import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { resolveImg } from "@/lib/api";
import { ReservationForm } from "@/components/site/ReservationForm";
import { PartyPopper, Cake, Sparkles, Phone } from "lucide-react";

export const EventsSection = ({ settings }) => {
  const { lang, pick } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const yB = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  if (!settings?.events_enabled) return null;
  const imgs = (settings?.events_images || []).map(resolveImg).filter(Boolean);
  if (!imgs.length) return null;

  const phone = (settings?.phone_1 || "").replace(/[^0-9]/g, "");
  const features = lang === "es"
    ? [{ i: PartyPopper, t: "Decoración a tu gusto" }, { i: Cake, t: "Pasteles de la casa" }, { i: Sparkles, t: "Catering y montaje" }]
    : [{ i: PartyPopper, t: "Custom decoration" }, { i: Cake, t: "In-house cakes" }, { i: Sparkles, t: "Catering & setup" }];

  return (
    <section id="events" ref={ref} className="py-24 md:py-32 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 right-0 w-[34rem] h-[34rem] rounded-full bg-maize/15 blur-3xl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 items-center">
          {/* left: copy */}
          <div>
            <Reveal>
              <p className="overline text-terracotta mb-4">{lang === "es" ? "Salón de Eventos" : "Event Hall"}</p>
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink">
                {pick(settings, "events_title")}
              </h2>
              <p className="font-body text-ink/70 mt-6 leading-relaxed max-w-md">{pick(settings, "events_text")}</p>
            </Reveal>

            <Reveal delay={0.15}>
              <ul className="mt-8 space-y-3">
                {features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 font-label font-medium text-ink">
                    <span className="grid place-items-center w-9 h-9 rounded-full bg-terracotta/10 text-terracotta">
                      <f.i size={17} />
                    </span>
                    {f.t}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.25}>
              <a
                href="#reservar"
                onClick={(e) => { e.preventDefault(); document.getElementById("reservar")?.scrollIntoView({ behavior: "smooth" }); if (window.lenis) window.lenis.scrollTo("#reservar", { offset: -80 }); }}
                data-testid="events-reserve-btn"
                className="inline-flex items-center gap-2 mt-9 bg-ink text-dough font-label font-bold rounded-full px-7 py-4 hover:bg-terracotta transition-colors duration-300"
              >
                <Phone size={18} /> {lang === "es" ? "Reservar salón" : "Book the hall"}
              </a>
            </Reveal>
          </div>

          {/* right: photo collage */}
          <div className="grid grid-cols-2 gap-4 md:gap-5">
            <motion.div style={{ y: yA }} className="space-y-4 md:space-y-5">
              {imgs.filter((_, i) => i % 2 === 0).map((src, i) => (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className={`overflow-hidden shadow-2xl shadow-ink/15 group ${i === 0 ? "arch" : "rounded-2xl"}`}
                >
                  <img src={src} alt="evento" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </motion.div>
              ))}
            </motion.div>
            <motion.div style={{ y: yB }} className="space-y-4 md:space-y-5 pt-8 md:pt-12">
              {imgs.filter((_, i) => i % 2 === 1).map((src, i) => (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.1 + 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden rounded-2xl shadow-2xl shadow-ink/15 group"
                >
                  <img src={src} alt="evento" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* reservation form */}
        <div id="reservar" className="mt-20 md:mt-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center scroll-mt-28">
          <Reveal>
            <div>
              <p className="overline text-terracotta mb-4">{lang === "es" ? "Reserva Online" : "Book Online"}</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight text-ink">
                {lang === "es" ? "Cuéntanos de tu evento" : "Tell us about your event"}
              </h2>
              <p className="font-body text-ink/70 mt-5 leading-relaxed max-w-sm">
                {lang === "es"
                  ? "Elige la fecha, el tipo de celebración y cuántos invitados. Nosotros te contactamos para confirmar disponibilidad y detalles."
                  : "Pick your date, celebration type and number of guests. We'll reach out to confirm availability and details."}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ReservationForm settings={settings} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
